import React, { useEffect, memo, useMemo, useCallback, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import '../index.css'
import { useSelector } from 'react-redux'
import { getHrEnums, getHrForm } from '../../../store/hr/action-creators'
import {
  getCandidateId,
  getHrFormError,
  getHrFormLoading,
  getHrFormData,
  getVisibleFormData,
} from './selectors'
import HrTabs from './ui/hr-tabs/hr-tabs'
import { HStack } from '../../../ui/Stack'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { HrFormsHeader } from './ui/hr-forms/hr-form-header/hr-form-header'
import { useHrSaveResultForms } from './hooks/useHrSaveResultForms'
import { Modal } from 'antd'
import { isFormDataRequired } from '../../../../lib/utils/collections'
import { HrModal } from './ui/hr-modal'

const HRCandidatePage = memo(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const candidateId = useSelector(getCandidateId)
  const hrFormIsLoading = useSelector(getHrFormLoading)
  const hrFormError = useSelector(getHrFormError)
  const hrFormData = useSelector(getHrFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const entityType = 'hr'
  const { saveResult } = useHrSaveResultForms()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isDisabled, setIsDisabled] = useState<boolean>()
  const formRef = useRef<typeof visibleFormData>()

  const pageTitle = useMemo(() => (id === 'new' ? 'Новая запись' : `Запись №${id}`), [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/hr',
        title: 'Учет кандидатов',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  useEffect(() => {
    if (visibleFormData) {
      formRef.current = visibleFormData
    }
  }, [visibleFormData])

  useEffect(() => {
    dispatch(getHrEnums())
  }, [dispatch])

  useEffect(() => {
    if (id === 'new' && candidateId) {
      navigate({
        pathname: `/hr/${candidateId}`,
      })
    }
  }, [id, candidateId, navigate])

  useEffect(() => {
    dispatch(getHrForm(id))
  }, [id, dispatch])

  const saveCase = useCallback(
    async (isNextStage = false) => {
      if (!formRef.current) return
      await saveResult(
        id,
        formRef.current,
        hrFormData?.stages?.current,
        isOpenModal,
        setIsOpenModal,
        isNextStage,
      )
    },
    [id, saveResult, hrFormData, isOpenModal],
  )

  const onFormDataModal = useCallback(async ({ formData }) => {
    if (!formRef.current) {
      return
    }
    const isFilesEdit = JSON.stringify(formData) === JSON.stringify(formRef.current?.modal)
    if (isFilesEdit) {
      return
    }
    formRef.current.modal = { ...formData }
  }, [])

  const handleOk = useCallback(async () => {
    if (!formRef.current) {
      return
    }
    const isRequiredModal = isFormDataRequired(
      formRef.current.modal,
      formRef.current.modalFieldsSchema,
    )
    if (!isRequiredModal) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните все обязательные поля',
      })
      return
    }
    setIsDisabled(true)
    setIsOpenModal(false)
    await saveCase(true)
    setIsDisabled(false)
  }, [saveCase])

  const handleCancel = useCallback(() => {
    setIsOpenModal(false)
  }, [])

  const handleOnClick = useCallback(async () => {
    setIsDisabled(true)
    await saveCase(true)
    setIsDisabled(false)
  }, [saveCase])

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {hrFormError && !hrFormIsLoading ? (
        <HStack gap={'8'} className={'mapWrapper'}>
          При выполнении запроса возникла ошибка.
          <br />
          {hrFormError}
          <br />
          Попробуйте перезагрузить страницу или открыть карточку повторно.
        </HStack>
      ) : (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <HrFormsHeader
            id={id}
            stage={hrFormData?.stages?.current}
            nextStageOnClick={handleOnClick}
            isDisabled={isDisabled}
            showTitle={false}
          />
          <HrTabs id={id} />
          {visibleFormData && !hrFormIsLoading && visibleFormData?.modal && (
            <HrModal
              schema={visibleFormData.modalFieldsSchema}
              onChange={onFormDataModal}
              formData={visibleFormData.modal}
              isOpen={isOpenModal}
              onCancel={handleCancel}
              onOk={handleOk}
              centered={true}
              disabled={isDisabled}
              title={visibleFormData?.modalTitle}
            />
          )}
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
})

HRCandidatePage.displayName = 'HRCandidatePage'
export default HRCandidatePage
